import api from './api';

/**
 * Marktplatz-Service (Marktplatz-Rework T1, Plan 4 „Fundament").
 *
 * Ersetzt den `fetch()`-Code aus `pages/MarketplacePage.tsx` durch die
 * Axios-Instanz aus `api.ts` (Bearer-Token + 401-Refresh laufen zentral).
 * T5 (Seller-Dashboard) und T6 (Buyer-Seite) hängen hier ihre Funktionen an
 * (Checkout, Statements, Billing-Profil, Kategorien, …) — Datei bleibt die
 * eine Stelle für alle `/api/marketplace/*`-Aufrufe.
 */

export type MarketplaceLicenseType = 'reference' | 'copy' | 'seat_copy';
export type ListingVisibility = 'public' | 'group';
export type ListingOrigin = 'own' | 'group' | 'public';
export type ListingStatus = 'draft' | 'in_review' | 'published' | 'rejected' | 'withdrawn';
export type GroupPricing = 'same' | 'free' | 'internal';

/** Kurzform eines Kurses im Angebot (Karten, Listen, Seller-Tabelle). */
export interface ListingCourseSummary {
    id: number;
    title: string;
    lessonCount?: number;
    /** Geschätzte Dauer in Minuten */
    duration?: number | null;
}

/** Kurs eines Bundles auf der Detailseite inkl. Abschnitten und Lektionen (nur Titel/Typ/Dauer). */
export interface ListingCourseDetail extends ListingCourseSummary {
    description?: string | null;
    difficulty?: string;
    estimatedDuration?: number | null;
    tags?: string[] | null;
    sections?: Array<{ id: number; title: string }>;
    lessons?: Array<{ id: number; title: string; type?: string; estimatedDuration?: number | null; sectionId?: number | null }>;
}

export interface MarketplaceListing {
    id: number;
    uuid: string;
    slug?: string | null;
    title: string;
    description: string | null;
    thumbnailUrl: string | null;
    course: {
        id: number;
        title?: string;
        difficulty?: string;
        estimatedDuration?: number;
        lessonCount?: number;
    };
    /** Alle Kurse des Angebots (Primärkurs zuerst); ältere Antworten ohne Feld = Einzelkurs */
    courses?: ListingCourseSummary[];
    isBundle?: boolean;
    courseCount?: number;
    /** Bereits lizenzierte Kurse dieses Angebots (Bundle: Kauf nicht möglich, solange einer lizenziert ist) */
    licensedCourseIds?: number[];
    /** Effektiver Preis für den Betrachter (Gruppenpreis bei Gruppenmitgliedern) */
    priceReference: string | null;
    priceCopy: string | null;
    priceSeat: string | null;
    /** Listenpreis (zum Durchstreichen, wenn abweichend) */
    listPriceReference?: string | null;
    listPriceCopy?: string | null;
    listPriceSeat?: string | null;
    origin?: ListingOrigin;
    groupPricing?: GroupPricing;
    priceGroup?: string | null;
    status?: ListingStatus;
    currency: string;
    allowReference: boolean;
    allowCopy: boolean;
    allowSeatCopy: boolean;
    platformFeePercent?: string;
    isFeatured: boolean;
    tags: string[] | null;
    highlights: string[] | null;
    previewVideoUrl: string | null;
    totalLicenses: number;
    averageRating: string | null;
    alreadyLicensed?: boolean;
    companyId: number;
    createdAt?: string;
    visibility?: ListingVisibility;
    category?: { id: number; name: string; slug: string } | null;
}

export interface CourseLicense {
    id: number;
    uuid: string;
    licenseType: string;
    status: string;
    pricePaid: string;
    licensorRevenue?: string;
    platformFee?: string;
    currency: string;
    maxEnrollments: number | null;
    currentEnrollments: number;
    expiresAt: string | null;
    origin?: ListingOrigin;
    mediaMigrationStatus?: 'pending' | 'running' | 'done' | 'failed';
    mediaMigrationError?: string | null;
    revokedAt?: string | null;
    revokeReason?: string | null;
    createdAt: string;
    sourceCourse: {
        id: number;
        title: string;
        thumbnailUrl?: string;
    };
    licensedCourse?: {
        id: number;
        title: string;
    } | null;
    listing?: {
        id: number;
        title: string;
        /** Lizenz stammt aus einem Bundle (mehrere Kurse in einem Angebot) */
        isBundle?: boolean;
        courseCount?: number;
    };
    licensorCompanyId?: number;
    licenseeCompanyId?: number;
}

// ---------------------------------------------------------------- Seller-Dashboard (T5)

export type ListingEditMode = 'all' | 'content' | 'none';
export type ListingAction = 'submit' | 'withdraw' | 'delete';
export type ListingWarning = 'billing_incomplete' | 'paypal_not_connected';

/** Angebot aus `GET /marketplace/my-listings` (Seller-Sicht inkl. Lebenszyklus). */
export interface SellerListing {
    id: number;
    uuid: string;
    slug: string | null;
    title: string;
    customTitle: string | null;
    description: string | null;
    thumbnailUrl: string | null;
    course: { id: number; title?: string };
    /** Alle Kurse des Angebots in Reihenfolge, Primärkurs zuerst */
    courses?: ListingCourseSummary[];
    isBundle?: boolean;
    courseCount?: number;
    visibility: ListingVisibility;
    groupPricing: GroupPricing;
    priceGroup: string | null;
    priceReference: string | null;
    priceCopy: string | null;
    priceSeat: string | null;
    currency: string;
    allowReference: boolean;
    allowCopy: boolean;
    allowSeatCopy: boolean;
    status: ListingStatus;
    rejectionReason: string | null;
    publishedAt: string | null;
    platformFeePercent: string;
    category: { id: number; name: string; slug: string } | null;
    tags: string[] | null;
    highlights: string[] | null;
    previewVideoUrl: string | null;
    totalLicenses: number;
    createdAt: string;
    editMode: ListingEditMode;
    actions: ListingAction[];
    warnings: ListingWarning[];
}

export interface SellerListingPayload {
    courseId?: number;
    /** Geordnete Kurse des Angebots (mind. 1, erster = Primärkurs; bei mehreren ist `title` Pflicht) */
    courseIds?: number[];
    title?: string | null;
    description?: string | null;
    thumbnailUrl?: string | null;
    previewVideoUrl?: string | null;
    priceReference?: string | null;
    priceCopy?: string | null;
    priceSeat?: string | null;
    allowReference?: boolean;
    allowCopy?: boolean;
    allowSeatCopy?: boolean;
    visibility?: ListingVisibility;
    groupPricing?: GroupPricing;
    priceGroup?: string | null;
    tags?: string[] | null;
    highlights?: string[] | null;
    categoryId?: number | null;
}

export interface SellerListingsResult {
    listings: SellerListing[];
    group: { hasGroup: boolean; memberCount: number };
}

export type SellerPeriod = '30d' | '90d' | '365d' | 'all';

export interface SellerKpis {
    grossRevenue: string;
    netRevenue: string;
    platformFeeTotal: string;
    salesCount: number;
    publicCount: number;
    groupFreeCount: number;
    groupInternalCount: number;
    internalChargeTotal: string;
    currentPeriodNet: string;
    paidOutYear: string;
}

export interface SellerSale {
    id: number;
    uuid: string;
    createdAt: string | null;
    courseTitle: string | null;
    listingId: number | null;
    buyerCompanyId: number;
    buyerCompanyName: string | null;
    origin: ListingOrigin;
    status: string;
    licenseType: MarketplaceLicenseType;
    seats: number | null;
    pricePaid: string;
    platformFee: string;
    licensorRevenue: string;
    currency: string;
}

export interface SellerReadiness {
    billingComplete: boolean;
    hasBankAccount: boolean;
    paypalStatus: 'not_started' | 'pending' | 'completed' | 'revoked';
    paypalReady: boolean;
    paymentMode: 'multiparty' | 'direct';
    missing: Array<'billing_data' | 'bank_account' | 'paypal'>;
}

export interface SellerDashboard {
    period: { key: SellerPeriod; from: string | null; to: string };
    currency: string;
    kpis: SellerKpis;
    monthly: Array<{ month: string; gross: string; net: string; count: number }>;
    recentSales: SellerSale[];
    listings: Record<ListingStatus, number>;
    readiness: SellerReadiness;
}

export interface SellerStatement {
    id: number;
    number: string | null;
    periodStart: string | null;
    periodEnd: string | null;
    grossTotal: string;
    refundTotal: string;
    platformFeeTotal: string;
    settlementNet: string;
    settlementMode: string;
    currency: string;
    status: 'open' | 'paid' | 'void';
    paidAt: string | null;
    paymentReference: string | null;
    lines: Array<Record<string, any>>;
}

export interface SellerBillingProfile {
    legalName: string | null;
    displayName: string | null;
    street: string | null;
    zip: string | null;
    city: string | null;
    country: string | null;
    taxNumber: string | null;
    vatId: string | null;
    vatIdValidatedAt: string | null;
    smallBusiness: boolean;
    billingEmail: string | null;
    iban: string | null;
    bic: string | null;
    accountHolder: string | null;
    logoUrl: string | null;
    primaryColor: string | null;
    paypalOnboardingStatus: SellerReadiness['paypalStatus'];
    paypalPaymentsReceivable: boolean;
    paypalConnected: boolean;
    paymentMode: 'multiparty' | 'direct';
    syncedAt: string | null;
    completeForInvoicing: boolean;
}

export interface SellerGroupMembers {
    hasGroup: boolean;
    members: Array<{ id: number; name: string | null }>;
    total: number;
}

// ---------------------------------------------------------------- Buyer-Seite (T6)

export type ListingSettlement = 'paypal' | 'free' | 'internal';

/** Detailansicht `GET /marketplace/listings/{slug}` (Kurs inkl. Lektionsliste). */
export interface MarketplaceListingDetail extends Omit<MarketplaceListing, 'course'> {
    course: {
        id: number;
        title?: string;
        description?: string | null;
        difficulty?: string;
        estimatedDuration?: number;
        tags?: string[] | null;
        lessonCount?: number;
        sections?: Array<{ id: number; title: string }>;
        lessons?: Array<{ id: number; title: string; type?: string; estimatedDuration?: number | null; sectionId?: number | null }>;
    };
    /** Alle Kurse mit Abschnitten und Lektionen (Primärkurs zuerst) */
    courses?: ListingCourseDetail[];
    /** Anbieter-Karte: Anzeigename aus dem Rechnungsprofil (kann fehlen) und Zahl sichtbarer Angebote */
    seller?: { name: string | null; listingCount: number };
}

export interface ListingDetailResult {
    listing: MarketplaceListingDetail;
    existingLicense: { id: number; licenseType: string; status: string; createdAt: string | null } | null;
}

/** `GET /marketplace/payment-config?listing=` — nie Secrets; `sellerMerchantId` nur im Multiparty-Modus. */
export interface PaymentConfig {
    clientId: string | null;
    env: 'sandbox' | 'live';
    currency: string;
    configured: boolean;
    sellerMerchantId?: string | null;
    partnerAttributionId?: string | null;
    paymentMode?: 'multiparty' | 'direct' | null;
    sellerCanReceivePayments?: boolean;
}

export interface CheckoutPayload {
    listingId: number;
    licenseType: MarketplaceLicenseType;
    seats?: number;
    catalogId?: number;
}

/** Antwort von `POST /marketplace/checkout` — `requires_payment` (PayPal) oder `completed` (0 €/intern). */
export interface CheckoutResponse {
    orderId: string;
    orderNumber: string | null;
    status: 'requires_payment' | 'completed';
    orderStatus: string;
    paypalOrderId: string | null;
    sellerMerchantId: string | null;
    total: string;
    netTotal: string;
    taxTotal: string;
    taxRate: string;
    currency: string;
}

export interface CaptureResponse {
    outcome: string;
    orderId: string;
    orderNumber: string | null;
    status: string;
    paymentStatus: string;
    total: string;
    currency: string;
    paidAt: string | null;
}

export interface BuyerOrderItem {
    title: string | null;
    licenseType: MarketplaceLicenseType | null;
    seats: number | null;
    listingId: number | null;
    listingSlug: string | null;
    licenseId: number | null;
    /** Bundles: Zahl der Lizenzen (eine je Kurs) und der Kurse der Position */
    licenseCount?: number;
    courseCount?: number;
}

/** Zeile der Bestellhistorie `GET /marketplace/orders`. */
export interface BuyerOrder {
    orderId: string;
    orderNumber: string | null;
    status: string;
    paymentStatus: string;
    total: string;
    netTotal: string;
    taxTotal: string;
    currency: string;
    createdAt: string | null;
    paidAt: string | null;
    vatTreatment: string;
    sellerCompanyId: number | null;
    settlement: ListingSettlement;
    items: BuyerOrderItem[];
}

/**
 * Rechnung aus `GET /marketplace/invoices` (Empfänger-Sicht, T4). Das PDF liefert
 * `GET /marketplace/invoices/{uuid}/pdf`. Belastungsanzeigen für interne
 * Verrechnungspreise kommen als `documentType=group_transfer` und sind kein
 * steuerlicher Beleg.
 */
export interface MarketplaceInvoice {
    id?: number;
    uuid: string;
    invoiceNumber: string;
    documentType: 'invoice' | 'cancellation' | 'credit_note' | 'group_transfer' | string;
    status?: string;
    total: string;
    currency: string;
    issueDate: string | null;
    orderNumber?: string | null;
    orderUuid?: string | null;
    issuerName?: string | null;
}

interface ListingsResponse {
    success: boolean;
    listings: MarketplaceListing[];
    total: number;
}

interface LicensesResponse {
    success: boolean;
    licenses: CourseLicense[];
    revenue?: any[];
}

/** API-Platform-Fehler (`detail`) oder Controller-Fehler (`error`) als lesbare Meldung. */
export const extractErrorMessage = (err: any, fallback: string): string =>
    err?.response?.data?.detail
    ?? err?.response?.data?.error
    ?? err?.response?.data?.message
    ?? err?.message
    ?? fallback;

export interface SellerReviewItem {
    id: number;
    listingId: number | null;
    listingTitle: string | null;
    rating: number;
    title: string | null;
    body: string | null;
    reviewerName: string | null;
    sellerReply: string | null;
    createdAt: string;
}

export interface SellerReviewOverview {
    reviews: SellerReviewItem[];
    count: number;
    average: string | null;
}

export const marketplaceService = {
    // ---------------------------------------------------------------- Browse / Buyer
    async browse(params?: { featured?: boolean; tag?: string }): Promise<MarketplaceListing[]> {
        const response = await api.get<ListingsResponse>('/marketplace/browse', { params });
        return response.data.listings ?? [];
    },

    async getListingBySlug(slug: string): Promise<ListingDetailResult> {
        const response = await api.get<{ success: boolean } & ListingDetailResult>(`/marketplace/listings/${encodeURIComponent(slug)}`);
        return { listing: response.data.listing, existingLicense: response.data.existingLicense ?? null };
    },

    /** Konfiguration für das PayPal-JS-SDK (Partner-Client-ID, Env, Seller-`merchant-id`). */
    async getPaymentConfig(listingId?: number): Promise<PaymentConfig> {
        const response = await api.get<PaymentConfig>('/marketplace/payment-config', {
            params: listingId ? { listing: listingId } : undefined,
        });
        return response.data;
    },

    /** Legt Order (+ PayPal-Order bei bezahlpflichtigen Angeboten) an; 0 €/intern wird sofort abgeschlossen. */
    async checkout(payload: CheckoutPayload): Promise<CheckoutResponse> {
        const response = await api.post<CheckoutResponse>('/marketplace/checkout', payload);
        return response.data;
    },

    async captureOrder(orderId: string, paypalOrderId: string): Promise<CaptureResponse> {
        const response = await api.post<CaptureResponse>(`/marketplace/orders/${orderId}/capture`, { paypalOrderId });
        return response.data;
    },

    async getOrders(): Promise<BuyerOrder[]> {
        const response = await api.get<{ orders: BuyerOrder[] }>('/marketplace/orders');
        return response.data.orders ?? [];
    },

    /** Medienmigration einer Kopie-Lizenz erneut anstoßen (T3, idempotent). */
    async retryLicenseMedia(licenseId: number): Promise<{ mediaMigrationStatus: string }> {
        const response = await api.post<{ mediaMigrationStatus: string }>(`/marketplace/licenses/${licenseId}/retry-media`);
        return response.data;
    },

    async getInvoices(): Promise<MarketplaceInvoice[]> {
        const response = await api.get<{ invoices: MarketplaceInvoice[] }>('/marketplace/invoices');
        return response.data.invoices ?? [];
    },

    /** Rechnungs-PDF als Blob (Bearer-Token über den Axios-Interceptor). */
    async downloadInvoicePdf(uuid: string): Promise<Blob> {
        const response = await api.get<Blob>(`/marketplace/invoices/${uuid}/pdf`, { responseType: 'blob' });
        return response.data;
    },

    async getMyLicenses(): Promise<CourseLicense[]> {
        const response = await api.get<LicensesResponse>('/marketplace/licenses');
        return response.data.licenses ?? [];
    },

    // ---------------------------------------------------------------- Seller-Dashboard (T5)
    async getSellerListings(): Promise<SellerListingsResult> {
        const response = await api.get<{ listings: SellerListing[]; group?: SellerListingsResult['group'] }>('/marketplace/my-listings');
        return {
            listings: response.data.listings ?? [],
            group: response.data.group ?? { hasGroup: false, memberCount: 0 },
        };
    },

    /** Bereits in eigenen Angeboten verwendete Schlagwörter (Vorschläge der Chip-Eingabe). */
    async getSellerTags(): Promise<string[]> {
        const response = await api.get<{ tags: string[] }>('/marketplace/my-listings/tags');
        return response.data.tags ?? [];
    },

    async createSellerListing(payload: SellerListingPayload): Promise<SellerListing> {
        const response = await api.post<{ listing: SellerListing }>('/marketplace/my-listings', payload);
        return response.data.listing;
    },

    async updateSellerListing(id: number, payload: SellerListingPayload): Promise<SellerListing> {
        const response = await api.patch<{ listing: SellerListing }>(`/marketplace/my-listings/${id}`, payload);
        return response.data.listing;
    },

    async submitSellerListing(id: number): Promise<SellerListing> {
        const response = await api.post<{ listing: SellerListing }>(`/marketplace/my-listings/${id}/submit`);
        return response.data.listing;
    },

    async withdrawSellerListing(id: number): Promise<SellerListing> {
        const response = await api.post<{ listing: SellerListing }>(`/marketplace/my-listings/${id}/withdraw`);
        return response.data.listing;
    },

    async deleteSellerListing(id: number): Promise<void> {
        await api.delete(`/marketplace/my-listings/${id}`);
    },

    async getSellerDashboard(period: SellerPeriod = '365d'): Promise<SellerDashboard> {
        const response = await api.get<SellerDashboard>('/marketplace/seller/dashboard', { params: { period } });
        return response.data;
    },

    async getSellerSales(period: SellerPeriod = '365d', limit = 50, offset = 0) {
        const response = await api.get<{ sales: SellerSale[]; total: number; summary: Omit<SellerKpis, 'currentPeriodNet' | 'paidOutYear'> }>(
            '/marketplace/seller/sales',
            { params: { period, limit, offset } },
        );
        return response.data;
    },

    async getSellerStatements(): Promise<SellerStatement[]> {
        const response = await api.get<{ statements: SellerStatement[] }>('/marketplace/seller/statements');
        return response.data.statements ?? [];
    },

    async getSellerReviews(): Promise<SellerReviewOverview> {
        const response = await api.get<SellerReviewOverview>('/marketplace/seller/reviews');
        return response.data;
    },

    async getSellerGroupMembers(): Promise<SellerGroupMembers> {
        const response = await api.get<SellerGroupMembers>('/marketplace/seller/group-members');
        return response.data;
    },

    async getSellerBillingProfile(refresh = false): Promise<{ profile: SellerBillingProfile | null; readiness: SellerReadiness }> {
        const response = await api.get<{ profile: SellerBillingProfile | null; readiness: SellerReadiness }>(
            '/marketplace/seller/billing-profile',
            { params: refresh ? { refresh: 1 } : undefined },
        );
        return response.data;
    },

    /** Startet das PayPal-Partner-Onboarding (T2) und liefert die `actionUrl` für den Redirect. */
    async startPaypalOnboarding(): Promise<{ actionUrl?: string; status?: string }> {
        const response = await api.post<{ actionUrl?: string; status?: string }>('/marketplace/seller/paypal/onboarding');
        return response.data;
    },

    // ---------------------------------------------------------------- Hilfsdaten (generische Ressourcen)
    async getCategories(): Promise<{ id: number; name: string; slug: string }[]> {
        const response = await api.get('/marketplace_categories', {
            params: { itemsPerPage: 100 },
            headers: { Accept: 'application/ld+json' },
        });
        return response.data['hydra:member'] ?? response.data.member ?? [];
    },

    async getOwnCourses(): Promise<{ id: number; title: string; isActive: boolean }[]> {
        const response = await api.get('/courses', {
            params: { isActive: true, 'order[title]': 'ASC', itemsPerPage: 100 },
            headers: { Accept: 'application/ld+json' },
        });
        return response.data['hydra:member'] ?? response.data.member ?? [];
    },

    async getOwnCatalogs(companyId: number | null): Promise<{ id: number; name: string; isActive: boolean }[]> {
        const response = await api.get('/course_catalogs', {
            params: { companyId, itemsPerPage: 100 },
            headers: { Accept: 'application/ld+json' },
        });
        return response.data['hydra:member'] ?? response.data.member ?? [];
    },
};

export default marketplaceService;
